"use client";

import { useRef, useState, useMemo } from "react";

const escapeHtml = (input) => String(input ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const stripUrls = (input) => String(input ?? "").replace(/https?:\/\/\S+/gi, "").replace(/\b[a-z0-9.-]+\.(com|net|org|nl|de|fr|it|es|co|io)\b/gi, "").trim();

export default function AISearchEngine({storeName, slug, countryCode, translation, endpoint = "/api/promoagent"}) {
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState(false);
  const [html, setHtml] = useState(translation?.ai_text || "Find additional offers");

  const reqId = useRef(0);

  // ✅ Normalize once, safely
  const normalizedStore = useMemo( () => String(storeName || "").trim(), [storeName] );

  const normalizedSlug = useMemo( () => String(slug || "").trim(), [slug] );

  const onClick = async () => {
    // Guard: no useless calls
    if (!normalizedStore && !normalizedSlug) return;

    const myId = ++reqId.current;
    setLoading(true);
    setFound(false);

    const start = Date.now();

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        body: JSON.stringify({
          store: normalizedStore,
          slug: normalizedSlug,
          countryCode,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const deals = Array.isArray(data?.deals) ? data.deals : [];

      const elapsed = Date.now() - start;
      if (elapsed < 900) {
        await new Promise((r) => setTimeout(r, 900 - elapsed));
      }

      if (myId !== reqId.current) return;

      if (!deals.length) {
        setFound(false);
        setHtml(
          `<div class="muted">${
            translation?.ai_none_found ??
            "All available coupon codes are already listed below."
          }</div>`
        );
        return;
      }

      const rows = deals
        .map((d) => {
          const code = stripUrls(d.code || "");
          const discount = stripUrls(d.discount || "");
          const conditions = stripUrls(d.conditions || "");

          return `
            <div class="row">
              <div class="cell code">${
                code
                  ? `<strong>${escapeHtml(code)}</strong>`
                  : `<span class="muted"></span>`
              }</div>
              <div class="cell discount">${
                discount
                  ? escapeHtml(discount)
                  : `<span class="muted"></span>`
              }</div>
              <div class="cell conditions">${
                conditions
                  ? escapeHtml(conditions)
                  : `<span class="muted"></span>`
              }</div>
            </div>
          `;
        })
        .join("");

      setFound(true);
      setHtml(`<div class="promo-deals">${rows}</div>`);
    } catch (e) {
      if (myId !== reqId.current) return;

      setFound(false);
      setHtml(
        `<div class="muted">${
          translation?.ai_none_found ??
          "All available coupon codes are already listed below."
        }</div>`
      );
    } finally {
      if (myId === reqId.current) setLoading(false);
    }
  };

  return (
    <div
      className="codelist-item"
      style={{ display: "flex", flexDirection: "column" }}
    >
      <button
        type="button"
        className="promo-agent-btn"
        onClick={onClick}
        disabled={loading}
        aria-busy={loading ? "true" : "false"}
      >
        {translation?.ai_button ?? "Find working coupons"}
      </button>

      <div id="promo-agent-box" className="promo-agent" data-nosnippet>
        {loading && (
          <div className="promo-agent-loading">
            <div className="spinner" />
            <div>
              {translation?.ai_searching ?? "Searching in realtime..."}
            </div>
          </div>
        )}

        {!loading && (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        )}

        <center
          style={{
            margin: "20px 0 0",
            display: found ? "block" : "none",
            fontSize: 13,
            opacity: 0.75,
          }}
        >
          {translation?.ai_found ?? "Live offers found by our AI Search Engine"}
        </center>
      </div>
    </div>
  );
}
